import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Zap,
  Rocket,
  Target,
  Heart,
  Globe,
  Users,
  Calendar,
  MapPin,
  Lightbulb,
  TrendingUp,
  Shield,
  CheckCircle2,
  Award,
  Briefcase,
} from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { Timeline } from "../../components/ui/Timeline";

const FOUNDER_IMAGE = "/assets/images/founder-tanzeel-khan.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

/* ─── HERO ─── */
const AboutHero = () => (
  <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 overflow-hidden hero-mesh">
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-violet-300/15 rounded-full blur-[120px]" />
      {/* morphing blob */}
      <div className="absolute top-1/2 right-1/4 w-72 h-72 bg-purple-400/10 rounded-full blur-[100px] animate-morph-blob" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left — text */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="max-w-3xl"
        >
          <motion.span
            variants={fadeUp}
            className="text-sm font-semibold text-violet-600 uppercase tracking-wider"
          >
            Our Story
          </motion.span>
          <motion.h1
            variants={fadeUp}
            custom={1}
            className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1]"
          >
            From a side project in Bangalore to an{" "}
            <span className="text-gradient">automation platform</span> for
            emerging markets.
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-4 sm:mt-6 text-base sm:text-lg text-slate-500 leading-relaxed"
          >
            We started in 2019 with memes and marketing. Today we're building
            the operating system for the next billion businesses. This is the
            story of how we got here, and why it matters.
          </motion.p>
          <motion.div variants={fadeUp} custom={3} className="mt-8">
            <Link
              to="#how-we-work"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-pink-500 text-white font-semibold hover:shadow-apple-lg hover:scale-[1.02] transition-all duration-300"
            >
              See How We Work <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>

        {/* Right — hero image (hidden on mobile) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block"
        >
          <img
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
            alt="Team collaboration"
            className="rounded-3xl shadow-apple-xl w-full object-cover animate-glow-pulse"
          />
          {/* Floating card — Est. 2019 */}
          <div className="absolute -bottom-4 -left-4 glass-strong rounded-xl px-4 py-2 shadow-apple">
            <p className="text-xs font-bold text-slate-800">Est. 2019</p>
          </div>
          {/* Floating card — 3 Cities */}
          <div className="absolute -top-4 -right-4 glass-strong rounded-xl px-4 py-2 shadow-apple">
            <p className="text-xs font-bold text-violet-700">
              Currently in 3 cities operating in India & overseas
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── TIMELINE ─── */
const IMG_SHADOW =
  "rounded-lg object-cover h-20 md:h-44 lg:h-60 w-full shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset]";

const timelineData = [
  {
    title: "2019",
    content: (
      <div>
        <h4 className="text-lg md:text-2xl font-bold text-slate-900 mb-2">
          The Beginning
        </h4>
        <p className="text-slate-600 text-xs md:text-sm font-normal mb-8">
          Started as a small marketing and meme marketing venture in Bangalore,
          India. Just two people with a laptop, a lot of hustle, and a dream to
          help local businesses go digital.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <img
            src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=500&q=80"
            alt="Early digital marketing setup"
            className={IMG_SHADOW}
            loading="lazy"
          />
          <img
            src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=500&q=80"
            alt="Small team brainstorming"
            className={IMG_SHADOW}
            loading="lazy"
          />
        </div>
      </div>
    ),
  },
  {
    title: "2020–22",
    content: (
      <div>
        <h4 className="text-lg md:text-2xl font-bold text-slate-900 mb-2">
          Finding Our Feet
        </h4>
        <p className="text-slate-600 text-xs md:text-sm font-normal mb-4">
          Evolved into a full-service digital agency: branding, video
          production, social media marketing. Served dozens of small businesses
          across India during the pandemic era.
        </p>
        <p className="text-slate-600 text-xs md:text-sm font-normal mb-8">
          Learned first-hand what small business owners actually need vs. what
          big agencies sell them.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <img
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=500&q=80"
            alt="Team workshop"
            className={IMG_SHADOW}
            loading="lazy"
          />
          <img
            src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=500&q=80"
            alt="Branding work"
            className={IMG_SHADOW}
            loading="lazy"
          />
          <img
            src="https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=500&q=80"
            alt="Social media marketing"
            className={IMG_SHADOW}
            loading="lazy"
          />
          <img
            src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=500&q=80"
            alt="Client presentation"
            className={IMG_SHADOW}
            loading="lazy"
          />
        </div>
      </div>
    ),
  },
  {
    title: "2023",
    content: (
      <div>
        <h4 className="text-lg md:text-2xl font-bold text-slate-900 mb-2">
          The IT Pivot
        </h4>
        <p className="text-slate-600 text-xs md:text-sm font-normal mb-8">
          Saw the AI revolution coming. Started building AI-powered solutions
          for clients. Launched our first chatbot and web development services.
          This was the turning point.
        </p>
        <div className="mb-8">
          <div className="flex gap-2 items-center text-slate-600 text-xs md:text-sm">
            <CheckCircle2 className="w-4 h-4 text-violet-600 flex-shrink-0" />
            First AI chatbot deployed
          </div>
          <div className="flex gap-2 items-center text-slate-600 text-xs md:text-sm">
            <CheckCircle2 className="w-4 h-4 text-violet-600 flex-shrink-0" />
            Expanded into web development
          </div>
          <div className="flex gap-2 items-center text-slate-600 text-xs md:text-sm">
            <CheckCircle2 className="w-4 h-4 text-violet-600 flex-shrink-0" />
            Entered the Middle East market
          </div>
          <div className="flex gap-2 items-center text-slate-600 text-xs md:text-sm">
            <CheckCircle2 className="w-4 h-4 text-violet-600 flex-shrink-0" />
            Team grew to 8 members
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <img
            src="https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=500&q=80"
            alt="AI technology"
            className={IMG_SHADOW}
            loading="lazy"
          />
          <img
            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=500&q=80"
            alt="Web development"
            className={IMG_SHADOW}
            loading="lazy"
          />
        </div>
      </div>
    ),
  },
  {
    title: "2024–25",
    content: (
      <div>
        <h4 className="text-lg md:text-2xl font-bold text-slate-900 mb-2">
          The Realization
        </h4>
        <p className="text-slate-600 text-xs md:text-sm font-normal mb-4">
          We realized something crucial: every automation tool was built for
          Silicon Valley budgets, not for the constraints and opportunities of
          India and the Middle East.
        </p>
        <p className="text-slate-600 text-xs md:text-sm font-normal mb-8">
          That's why we built BusiGrow. Not just another agency, but a platform
          built from the ground up for emerging markets and the teams that run
          them.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <img
            src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=500&q=80"
            alt="Strategic planning"
            className={IMG_SHADOW}
            loading="lazy"
          />
          <img
            src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=500&q=80"
            alt="Global expansion map"
            className={IMG_SHADOW}
            loading="lazy"
          />
          <img
            src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=500&q=80"
            alt="Team collaboration"
            className={IMG_SHADOW}
            loading="lazy"
          />
          <img
            src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=500&q=80"
            alt="Product ideation"
            className={IMG_SHADOW}
            loading="lazy"
          />
        </div>
      </div>
    ),
  },
  {
    title: "2026",
    content: (
      <div>
        <h4 className="text-lg md:text-2xl font-bold text-slate-900 mb-2">
          BusiGrow Automate
        </h4>
        <p className="text-slate-600 text-xs md:text-sm font-normal mb-4">
          Pivoted to a product company. Launched BusiGrow Automate: an
          AI-powered business automation platform built specifically for
          emerging markets.
        </p>
        <p className="text-slate-600 text-xs md:text-sm font-normal mb-8">
          Now operating across India, the Middle East & South East Asia. The
          journey continues. 🚀
        </p>
        <div className="grid grid-cols-2 gap-4">
          <img
            src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=500&q=80"
            alt="Modern tech office"
            className={IMG_SHADOW}
            loading="lazy"
          />
          <img
            src="https://images.unsplash.com/photo-1535378917042-10a22c95931a?auto=format&fit=crop&w=500&q=80"
            alt="AI automation platform"
            className={IMG_SHADOW}
            loading="lazy"
          />
        </div>
      </div>
    ),
  },
];

const TimelineSection = () => (
  <section className="py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="text-center"
      >
        <motion.span
          variants={fadeUp}
          className="text-sm font-semibold text-violet-600 uppercase tracking-wider"
        >
          Our Journey
        </motion.span>
        <motion.h2
          variants={fadeUp}
          custom={1}
          className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900"
        >
          7 years of evolution
        </motion.h2>
        <motion.p
          variants={fadeUp}
          custom={2}
          className="mt-4 text-slate-500 text-sm md:text-base max-w-lg mx-auto"
        >
          From a two-person meme marketing venture in Bangalore to an AI-powered
          automation platform for emerging markets. Here's how we got here.
        </motion.p>
      </motion.div>

      <div className="mt-10">
        <Timeline data={timelineData} />
      </div>
    </div>
  </section>
);

/* ─── MISSION & VALUES ─── */
const values = [
  {
    icon: Heart,
    title: "Market Empathy",
    desc: "We don't just serve emerging markets. We're built by people from them. We understand the WhatsApp-first, budget-conscious way businesses operate.",
  },
  {
    icon: Shield,
    title: "Honest by Default",
    desc: "No fake metrics. No inflated claims. We show what's real, and we're transparent about what we're still building.",
  },
  {
    icon: Globe,
    title: "Local-Global",
    desc: "Built in India for the world. We think in Hindi, execute in code, and dream in scale.",
  },
  {
    icon: Zap,
    title: "Ship Fast, Learn Faster",
    desc: "We're a startup, not a corporation. We ship weekly, talk to users daily, and pivot when the data tells us to.",
  },
];

const ValuesSection = () => (
  <section className="py-16 sm:py-24 bg-violet-tint">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="text-center max-w-3xl mx-auto"
      >
        <motion.span
          variants={fadeUp}
          className="text-sm font-semibold text-violet-600 uppercase tracking-wider"
        >
          What We Stand For
        </motion.span>
        <motion.h2
          variants={fadeUp}
          custom={1}
          className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight"
        >
          Our mission &amp; values
        </motion.h2>
        <motion.p
          variants={fadeUp}
          custom={2}
          className="mt-6 text-lg text-slate-700 font-medium"
        >
          Our mission is simple: make AI-powered automation accessible and
          affordable for every business in emerging markets.
        </motion.p>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6"
      >
        {values.map((v, i) => (
          <motion.div
            key={i}
            variants={fadeUp}
            custom={i}
            whileHover={{ y: -6, scale: 1.02 }}
            className="p-8 rounded-2xl glass shadow-apple hover:shadow-apple-lg transition-all card-shine border border-white/40 group"
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center mb-4 group-hover:shadow-lg transition-shadow">
              <v.icon className="w-7 h-7 text-violet-700 font-bold" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-3">{v.title}</h3>
            <p className="text-slate-700 leading-relaxed font-medium">
              {v.desc}
            </p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ─── FOUNDER ─── */
const FounderSection = () => (
  <section className="py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="relative rounded-3xl overflow-hidden shadow-apple-xl max-w-md mx-auto lg:mx-0">
            <img
              src={FOUNDER_IMAGE}
              alt="Tanzeel Khan"
              className="w-full h-[420px] sm:h-[480px] lg:h-[430px] object-cover img-parallax"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 glass-strong rounded-xl px-4 py-2">
              <p className="text-sm font-bold text-slate-800">Tanzeel Khan</p>
              <p className="text-xs text-slate-500">Founder & CEO</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.span
            variants={fadeUp}
            className="text-sm font-semibold text-violet-600 uppercase tracking-wider"
          >
            The Founder
          </motion.span>
          <motion.h2
            variants={fadeUp}
            custom={1}
            className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight"
          >
            Meet Tanzeel Khan
          </motion.h2>
          <motion.div
            variants={fadeUp}
            custom={2}
            className="mt-6 space-y-4 text-slate-500 leading-relaxed"
          >
            <p>
              Tanzeel Khan founded BusiGrow with a clear mission: build
              practical, high-impact solutions that help businesses scale with
              confidence.
            </p>
            <p>
              His leadership combines product vision, execution speed, and
              market empathy, shaping how we build and deliver across every
              vertical.
            </p>
            <p>
              Today, he continues to lead BusiGrow's growth with a strong focus
              on innovation, customer outcomes, and long-term value creation.
            </p>
          </motion.div>
          <motion.div
            variants={fadeUp}
            custom={3}
            className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-6 border-t border-slate-200"
          >
            <div className="flex items-center gap-3 text-sm font-semibold text-slate-700">
              <div className="w-2 h-2 rounded-full bg-violet-600" />
              <MapPin className="w-5 h-5 text-violet-600" />
              <span>Bangalore, India</span>
            </div>
            <div className="flex items-center gap-3 text-sm font-semibold text-slate-700">
              <div className="w-2 h-2 rounded-full bg-violet-600" />
              <Calendar className="w-5 h-5 text-violet-600" />
              <span>Building since 2019</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── TEAM ─── */
const TeamSection = () => (
  <section className="py-16 sm:py-24 bg-violet-tint">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="text-center mb-12"
      >
        <motion.span
          variants={fadeUp}
          className="text-sm font-semibold text-violet-600 uppercase tracking-wider"
        >
          Our Team
        </motion.span>
        <motion.h2
          variants={fadeUp}
          custom={1}
          className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900"
        >
          The people behind{" "}
          <span className="text-gradient-vibrant">BusiGrow</span>
        </motion.h2>
      </motion.div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
        {[
          { name: "Tanzeel Khan", role: "Founder & CEO", img: FOUNDER_IMAGE },
          { name: "Prabal Choudhary", role: "Tech Lead", img: "" },
          { name: "Saad Saud Khan", role: "Marketing Lead", img: "" },
        ].map((m, i) => (
          <motion.div
            key={m.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            whileHover={{ y: -6 }}
            className="group card-shine rounded-2xl overflow-hidden shadow-apple hover:shadow-apple-xl transition-all"
          >
            <div className="relative h-48 sm:h-64 overflow-hidden">
              {m.img ? (
                <img
                  src={m.img}
                  alt={m.name}
                  className="w-full h-full object-cover img-parallax"
                  style={{ objectPosition: "center 30%" }}
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-slate-200 via-slate-100 to-white flex items-center justify-center">
                  <span className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                    Image coming soon
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
            </div>
            <div className="p-4 bg-white">
              <p className="text-sm font-bold text-slate-900">{m.name}</p>
              <p className="text-xs text-slate-500">{m.role}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── HIRING CTA ─── */
const HiringSection = () => (
  <section className="py-16 sm:py-24 bg-slate-50">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl overflow-hidden shadow-apple-xl"
        >
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=600&q=80"
            alt="Modern office space"
            className="w-full h-64 sm:h-80 object-cover"
            loading="lazy"
          />
        </motion.div>

        {/* Text */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center lg:text-left"
        >
          <motion.span
            variants={fadeUp}
            className="text-sm font-semibold text-violet-600 uppercase tracking-wider"
          >
            Join Us
          </motion.span>
          <motion.h2
            variants={fadeUp}
            custom={1}
            className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900"
          >
            We're building the team
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-4 text-lg text-slate-500 max-w-2xl mx-auto lg:mx-0"
          >
            We're looking for backend engineers, AI/ML engineers, and designers
            who want to build products that impact millions of businesses.
            Remote-first. India &amp; Middle East based.
          </motion.p>
          <motion.div variants={fadeUp} custom={3} className="mt-8">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold hover:shadow-apple-lg transition-all"
            >
              View Open Roles
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── PROBLEM STATEMENT ─── */
const ProblemSection = () => (
  <section className="py-16 sm:py-24 bg-white relative overflow-hidden">
    <div className="absolute inset-0 opacity-30">
      <div className="absolute top-20 left-20 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
    </div>
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8 leading-tight">
            The problem we're solving
          </h2>
          <div className="space-y-6 text-lg leading-relaxed">
            <p className="text-slate-600 font-medium">
              Emerging markets have{" "}
              <span className="text-violet-600 font-bold">
                2+ billion businesses
              </span>
              , yet{" "}
              <span className="text-violet-600 font-bold">
                90% operate without any automation
              </span>
              . They're stuck using WhatsApp, Excel, and phone calls.
            </p>
            <p className="text-slate-600 font-medium">
              Every existing tool is built for{" "}
              <span className="text-violet-600 font-semibold">
                Western markets and workflows
              </span>
              assuming constant connectivity, complex setups, and online-only
              operations. They don't work for India, Southeast Asia, or other
              emerging markets.
            </p>
            <div className="mt-8 p-6 rounded-xl bg-purple-50/40 border border-purple-200/30 backdrop-blur-sm">
              <p className="font-bold text-slate-900 text-lg">
                <span className="text-violet-600">
                  ✓ BusiGrow was born to change that.
                </span>{" "}
                We're building solutions designed for{" "}
                <span className="text-violet-600 font-bold">
                  offline-first operation
                </span>
                ,{" "}
                <span className="text-violet-600 font-bold">
                  WhatsApp-native workflows
                </span>
                , and the realities of emerging markets.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ─── IMPACT SECTION ─── */
const ImpactSection = () => (
  <section className="py-16 sm:py-24 bg-gradient-to-b from-violet-50/40 to-transparent">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="text-center mb-12"
      >
        <motion.h2
          variants={fadeUp}
          className="text-3xl sm:text-4xl font-bold text-slate-900"
        >
          Our Impact & Credentials
        </motion.h2>
        <motion.p
          variants={fadeUp}
          custom={1}
          className="mt-4 text-slate-600 text-lg max-w-2xl mx-auto font-medium"
        >
          What we've built and who trusts us
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            icon: Users,
            stat: "500+",
            label: "Active Users",
            detail: "Across India & Middle East",
          },
          {
            icon: Award,
            stat: "7 Years",
            label: "Industry Experience",
            detail: "Since 2019",
          },
          {
            icon: Briefcase,
            stat: "3 Cities",
            label: "Operating Across",
            detail: "Currently in India & overseas",
          },
          {
            icon: TrendingUp,
            stat: "40%",
            label: "Avg Growth",
            detail: "For our clients yearly",
          },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={i}
            className="glass-strong rounded-2xl p-7 shadow-apple border border-white/50 hover:shadow-apple-xl transition-all"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center mb-5">
              <item.icon className="w-6 h-6 text-violet-600 font-bold" />
            </div>
            <div className="text-4xl font-black text-slate-900 mb-2">
              {item.stat}
            </div>
            <div className="font-bold text-slate-900 text-base mb-2">
              {item.label}
            </div>
            <p className="text-sm text-slate-600 font-medium">{item.detail}</p>
          </motion.div>
        ))}
      </div>

      {/* Achievements List */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {[
          "Partnered with 500+ emerging market businesses",
          "Reduced operational costs by 50-70% for clients",
          "Built AI solutions processing 10M+ transactions/month",
          "Trained 200+ businesses on digital transformation",
          "Zero customer churn for last 12 months",
          "Fastest response time: <2 hours",
        ].map((achievement, i) => (
          <motion.div
            key={i}
            variants={fadeUp}
            custom={i}
            className="flex gap-4 items-start p-4 rounded-xl bg-purple-50/40 border border-purple-200/30 hover:bg-purple-50/60 transition-colors"
          >
            <CheckCircle2 className="w-6 h-6 text-violet-600 flex-shrink-0 mt-0.5 font-bold" />
            <p className="text-slate-800 font-semibold leading-relaxed">
              {achievement}
            </p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ─── APPROACH SECTION ─── */
const ApproachSection = () => (
  <section className="py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="text-center mb-12"
      >
        <motion.h2
          variants={fadeUp}
          className="text-3xl sm:text-4xl font-bold text-slate-900"
        >
          How We Work
        </motion.h2>
        <motion.p
          variants={fadeUp}
          custom={1}
          className="mt-4 text-slate-600 max-w-2xl mx-auto font-medium"
        >
          Our process is transparent, fast, and designed for your success
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          {
            num: "01",
            icon: Lightbulb,
            title: "Understand Your Needs",
            desc: "We spend time learning about your business, challenges, and goals. No templated solutions.",
            color: "from-violet-500 to-purple-600",
          },
          {
            num: "02",
            icon: Target,
            title: "Custom Solutions",
            desc: "We build or configure solutions specifically for your market, budget, and workflows.",
            color: "from-purple-500 to-pink-600",
          },
          {
            num: "03",
            icon: CheckCircle2,
            title: "Hands-On Support",
            desc: "We train your team, provide 24/7 support, and continuously optimize based on your feedback.",
            color: "from-pink-500 to-rose-600",
          },
        ].map((approach, i) => (
          <motion.div
            key={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={i}
            className="relative"
          >
            <div className="glass-strong rounded-2xl p-8 shadow-apple border border-white/40 hover:border-white/60 transition-all h-full">
              <div
                className={`inline-flex w-14 h-14 rounded-xl bg-gradient-to-br ${approach.color} items-center justify-center mb-6 shadow-lg`}
              >
                <approach.icon className="w-7 h-7 text-white" />
              </div>
              <div className="text-4xl font-black text-slate-200 mb-4 opacity-30">
                {approach.num}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 leading-tight">
                {approach.title}
              </h3>
              <p className="text-slate-600 leading-relaxed font-medium">
                {approach.desc}
              </p>
            </div>
            {i < 2 && (
              <div className="hidden md:block absolute top-12 -right-4 w-8 h-px bg-gradient-to-r from-violet-300 to-transparent" />
            )}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── CTA TO EXPLORE SOLUTIONS ─── */
const ExploreCtaSection = () => (
  <section className="py-12 sm:py-16">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 p-12 sm:p-16 text-center shadow-apple-xl"
      >
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/10 blur-2xl" />
        <div className="relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to transform your business?
          </h2>
          <p className="text-lg text-white/80 max-w-xl mx-auto mb-8">
            Whether you need automation, AI solutions, or strategic guidance,
            we're here to help.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/platform"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-violet-700 font-semibold rounded-xl hover:shadow-apple-lg transition-all"
            >
              Explore Our Solutions
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-all"
            >
              Let's Talk
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ─── PAGE ─── */
const AboutPage = () => (
  <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
    <Header />
    <AboutHero />
    <ProblemSection />
    <ImpactSection />
    <ApproachSection />
    <TimelineSection />
    <ValuesSection />
    <FounderSection />
    <TeamSection />
    <ExploreCtaSection />
    <HiringSection />
    <Footer />
  </div>
);

export default AboutPage;
